// Admin-only view switcher for the Token Usage page: "My usage" / "Specific
// user" / "All users", plus an OID input for the specific-user case. Only
// rendered by the page when useSwaAuth().isAdmin is true.
import { useEffect, useState } from 'react';
import {
  Body1,
  Button,
  Card,
  Field,
  Input,
  Radio,
  RadioGroup,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import type { RadioGroupOnChangeData } from '@fluentui/react-components';

export type AdminViewMode = 'self' | 'user' | 'all';

const useStyles = makeStyles({
  card: {
    padding: tokens.spacingVerticalM,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },
  row: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: tokens.spacingHorizontalM,
    flexWrap: 'wrap',
  },
});

export interface AdminUsageControlsProps {
  mode: AdminViewMode;
  onModeChange: (mode: AdminViewMode) => void;
  /** The OID currently applied to the "specific user" lookup (committed, not draft input). */
  lookupOid: string;
  onLookupOid: (oid: string) => void;
}

export function AdminUsageControls(props: AdminUsageControlsProps) {
  const { mode, onModeChange, lookupOid, onLookupOid } = props;
  const styles = useStyles();
  const [draftOid, setDraftOid] = useState(lookupOid);

  // Re-sync the draft input whenever the committed lookupOid changes from
  // OUTSIDE this component (e.g. clicking a row in the All-users table sets
  // lookupOid directly) -- otherwise the Input keeps showing whatever was
  // last typed/committed here, mismatched against the OID actually driving
  // the page's data, and a stray "Look up" click would silently revert the
  // view to the stale OID (see PR #96968 thread 958747).
  useEffect(() => {
    setDraftOid(lookupOid);
  }, [lookupOid]);

  const handleModeChange = (_: unknown, data: RadioGroupOnChangeData) => {
    onModeChange(data.value as AdminViewMode);
  };

  const submitLookup = () => {
    onLookupOid(draftOid.trim());
  };

  return (
    <Card className={styles.card}>
      <Body1><strong>Admin view</strong></Body1>
      <RadioGroup layout="horizontal" value={mode} onChange={handleModeChange}>
        <Radio value="self" label="My usage" />
        <Radio value="user" label="Specific user" />
        <Radio value="all" label="All users" />
      </RadioGroup>
      {mode === 'user' && (
        <div className={styles.row}>
          <Field label="User object ID (OID)" style={{ minWidth: '320px' }}>
            <Input
              value={draftOid}
              onChange={(_, data) => setDraftOid(data.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') submitLookup();
              }}
              placeholder="e.g. 11111111-2222-3333-4444-555555555555"
            />
          </Field>
          <Button appearance="primary" onClick={submitLookup} disabled={!draftOid.trim()}>
            Look up
          </Button>
        </div>
      )}
    </Card>
  );
}

export default AdminUsageControls;
